import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { authAPI } from '../services/apiService';
import JwtUtils from '../utils/jwtUtils';
import authStorage from '../utils/authStorage';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const idleLogoutRef = useRef(false);

  const LAST_ACTIVITY_KEY = 'nixia_last_activity_at';
  const IDLE_TIMEOUT_MS = 60 * 60 * 1000;
  const IDLE_CHECK_MS = 60 * 1000;
  const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

  const setLastActivity = useCallback(() => {
    authStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  }, []);

  const getLastActivity = useCallback(() => {
    const raw = authStorage.getItem(LAST_ACTIVITY_KEY);
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const clearSessionData = useCallback(() => {
    const keys = [
      'Nixia_active_organization_id',
      'nixia_active_organization_id',
      'Nixia_active_agent_id',
      'nixia_active_agent_id',
      'Nixia_active_agent_by_org',
      'Nixia_active_agent_org_id',
      'Nixia_onboarding_session',
      'onboarding_shown',
      'nixia_onboarding_done',
      LAST_ACTIVITY_KEY
    ];
    keys.forEach((key) => {
      authStorage.removeItem(key);
      if (typeof window !== 'undefined') {
        window.localStorage?.removeItem(key);
        window.sessionStorage?.removeItem(key);
      }
    });
  }, [LAST_ACTIVITY_KEY]);

  // Initialize: Check if user is already logged in by token validation
  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      try {
        // Verifier d'abord si nous avons un token valide localement
        const isLoggedIn = JwtUtils.validateAndCleanToken();

        if (isLoggedIn) {
          // Verifier le token avec le backend pour la securite
          const verificationResult = await authAPI.verifyToken();

          if (verificationResult.valid) {
            // Token valide sur le serveur, definir les donnees utilisateur
            let userData = JwtUtils.getStoredUser();

            // Always update user data from verification result to ensure user_type is present
            if (verificationResult.username) {
              userData = {
                username: verificationResult.username,
                id: verificationResult.user_id || verificationResult.admin_id,
                email: verificationResult.email,
                full_name: verificationResult.full_name,
                user_type: verificationResult.user_type || 'ADMIN',
                is_admin: verificationResult.user_type === 'admin',
                is_user: verificationResult.user_type === 'user'
              };

              // If user_type is 'user', decode the actual user type from membership
              if (verificationResult.user_type === 'user') {
                const tokenPayload = JwtUtils.decodeToken(JwtUtils.getStoredToken());
                userData.user_type = tokenPayload.user_type || 'AGENT_SIMPLE';
              }

              JwtUtils.storeUser(userData);
              console.log('User data updated with user_type:', userData.user_type);
            }

            setUser(userData);
            console.log('User authenticated successfully:', userData?.username, 'Type:', userData?.user_type);
            setLastActivity();
            window.dispatchEvent(new CustomEvent('authStateChanged'));
          } else if (verificationResult.error === 'Network error during verification') {
            // Garder la session locale si l'API est temporairement indisponible
            const localUser = JwtUtils.getStoredUser();
            if (localUser) {
              setUser(localUser);
              console.warn('Network issue during token verification, keeping local session');
              setLastActivity();
            } else {
              setUser(null);
            }
          } else {
            // Token invalide ou expire, tout nettoyer
            console.log('Token verification failed:', verificationResult.error);
            JwtUtils.removeToken();
            JwtUtils.removeUser();
            setUser(null);
            clearSessionData();
            window.dispatchEvent(new CustomEvent('authStateChanged'));

            if (verificationResult.expired) {
              setError('Votre session a expire. Veuillez vous reconnecter.');
            }
          }
        } else {
          console.log('No valid token found, user not authenticated');
          setUser(null);
        }
      } catch (err) {
        console.error('Auth initialization error:', err);

        // Fallback: conserver la session locale si le token n'est pas expire
        const localToken = JwtUtils.getStoredToken();
        const localUser = JwtUtils.getStoredUser();
        if (localToken && !JwtUtils.isTokenExpired(localToken) && localUser) {
          setUser(localUser);
          setError('Connexion au serveur temporairement indisponible.');
        } else {
          JwtUtils.removeToken();
          JwtUtils.removeUser();
          setUser(null);
          clearSessionData();
          setError('Echec de la verification d\'authentification');
        }
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [clearSessionData, setLastActivity]);

  // Login function
  const login = useCallback(async (username, password) => {
    setLoading(true);
    setError(null);

    try {
      console.log('Attempting login for:', username);
      const { success, user: userData, error: loginError } = await authAPI.login(username, password);

      if (success) {
        setUser(userData);
        console.log('Login successful for:', userData.username, 'Type:', userData.user_type);
        setLastActivity();
        window.dispatchEvent(new CustomEvent('authStateChanged'));
        return true;
      } else {
        setError(loginError || 'Nom d\'utilisateur ou mot de passe invalide');
        console.log('Login failed:', loginError);
        return false;
      }
    } catch (err) {
      setError('Echec de la connexion. Veuillez reessayer plus tard.');
      console.error('Login error:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    setLoading(true);
    try {
      console.log('Logging out user');
      JwtUtils.removeToken();
      JwtUtils.removeUser();
      clearSessionData();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setError(null);
      setLoading(false);
      window.dispatchEvent(new CustomEvent('authStateChanged'));
    }
  }, [clearSessionData]);

  // Get user profile data
  const getUserProfile = useCallback(async () => {
    if (!user) return null;

    try {
      return user;
    } catch (err) {
      console.error('Get user profile error:', err);
      return null;
    }
  }, [user]);

  // Clear error function
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Refresh token function
  const refreshAuth = useCallback(async () => {
    try {
      const verificationResult = await authAPI.verifyToken();
      if (!verificationResult.valid) {
        if (verificationResult.error === 'Network error during verification') {
          console.warn('Token refresh skipped (network issue), keeping current session');
          return true;
        }

        console.log('Token refresh failed, logging out');
        await logout();
        return false;
      }

      // Update user data with refreshed info including user_type
      if (verificationResult.username) {
        const userData = {
          username: verificationResult.username,
          id: verificationResult.user_id || verificationResult.admin_id,
          email: verificationResult.email,
          full_name: verificationResult.full_name,
          user_type: verificationResult.user_type || 'ADMIN',
          is_admin: verificationResult.user_type === 'admin',
          is_user: verificationResult.user_type === 'user'
        };

        if (verificationResult.user_type === 'user') {
          const tokenPayload = JwtUtils.decodeToken(JwtUtils.getStoredToken());
          userData.user_type = tokenPayload.user_type || 'AGENT_SIMPLE';
        }

        setUser(userData);
        JwtUtils.storeUser(userData);
        setLastActivity();
      }

      return true;
    } catch (err) {
      console.error('Auth refresh error:', err);
      // Ne pas forcer la deconnexion sur erreur reseau
      return true;
    }
  }, [logout]);

  useEffect(() => {
    if (!user) return undefined;
    idleLogoutRef.current = false;
    setLastActivity();

    const handleActivity = () => {
      if (idleLogoutRef.current) return;
      setLastActivity();
    };

    const checkIdle = async () => {
      if (idleLogoutRef.current) return;
      const last = getLastActivity();
      if (!last) return;
      if (Date.now() - last >= IDLE_TIMEOUT_MS) {
        idleLogoutRef.current = true;
        setError('Votre session a expiré par inactivité. Veuillez vous reconnecter.');
        await logout();
        window.location.href = '/admin/login';
      }
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    const intervalId = setInterval(checkIdle, IDLE_CHECK_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      clearInterval(intervalId);
    };
  }, [user, getLastActivity, logout, setLastActivity]);

  // useAuth()/useContext(AuthContext) is consumed across the top-level layout
  // and routing - without this memo every render of AuthProvider (e.g. from
  // the idle-check interval's setState calls) would hand consumers a new
  // object and re-render the whole authenticated tree.
  const value = useMemo(
    () => ({
      user,
      loading,
      error,
      login,
      logout,
      getUserProfile,
      clearError,
      refreshAuth,
      isAuthenticated: !!user && JwtUtils.isAuthenticated(),
    }),
    [user, loading, error, login, logout, getUserProfile, clearError, refreshAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


